/**
 * TMS API Client — fetch wrapper with auth + tenant headers
 */

import { attachUiText, isStoredMsg, sayStored } from "@/lib/ui-text-attach";
import { API_BASE, clearTokens, getAccessToken, refreshSession } from "./token-store";
import { HOST_ORG_MISMATCH } from "@9nerd/shared";
import { tOutside } from "@/lib/i18n";
import { getDisplayLocale } from "@/lib/display-locale";

const API_PREFIX = "api/v1";

if (
  process.env.NODE_ENV === "production" &&
  !process.env.NEXT_PUBLIC_API_URL
) {
  // eslint-disable-next-line no-console
  console.error(
    "[api] NEXT_PUBLIC_API_URL is not set in production; falling back to http://localhost:17003 which will fail.",
  );
}

function getToken(): string | null {
  return getAccessToken();
}

function getTenantId(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem("active_tenant_id");
}

/**
 * ADR 0156 R7 — a request with no ceiling never fails, it just stops.
 *
 * `fetch` has no default timeout. On a flaky connection a hung request sits
 * forever: a poller that guards against overlapping calls stalls, and the
 * screen freezes showing stale data with no error and no spinner. A ceiling
 * turns that silent freeze into something the caller can retry.
 *
 * 30s, not the 8s the War Room wants: this wrapper serves every page in the
 * admin app, including exports that legitimately take a while (exceljs). Callers
 * that need to fail faster pass their own `timeoutMs` — the War Room's polled
 * reads use 8s, matching the 5s `maxTimeMS` ceiling on the server plus headroom.
 *
 * An explicit `signal` wins: callers already managing cancellation are not
 * second-guessed.
 */
const DEFAULT_TIMEOUT_MS = 30_000;

interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Override the request ceiling. Ignored when the caller passes its own `signal`. */
  timeoutMs?: number;
}

export async function api<T = unknown>(
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  const token = getToken();
  const tenantId = getTenantId();

  const headers: Record<string, string> = {
    ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...(opts.headers as Record<string, string>),
  };

  if (token) headers["Authorization"] = `Bearer ${token}`;
  // A caller-supplied x-tenant-id wins (ADR 0418 D11: super admin uploads a branding file FOR the
  // org being edited, not the one in context). Everyone else gets the active tenant as before.
  if (tenantId && !headers["x-tenant-id"]) headers["x-tenant-id"] = tenantId;

  const url = `${API_BASE}/${API_PREFIX}/${path.replace(/^\//, "")}`;

  const { timeoutMs, ...init } = opts;
  // A fresh signal per attempt — the retry below must not inherit a timer that
  // has already been running for the whole first attempt.
  const signalFor = () =>
    init.signal ?? AbortSignal.timeout(timeoutMs ?? DEFAULT_TIMEOUT_MS);

  const res = await fetch(url, {
    ...init,
    headers,
    signal: signalFor(),
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401) {
    // Single-flight refresh (R9) — concurrent 401s share one refresh; the old
    // per-caller BFF refresh raced itself and killed the session (ADR 0052).
    const refreshed = await refreshSession().catch(() => false);
    if (refreshed) {
      const newToken = getToken();
      if (newToken) headers["Authorization"] = `Bearer ${newToken}`;
      const retry = await fetch(url, {
        ...init,
        headers,
        signal: signalFor(),
        body: opts.body ? JSON.stringify(opts.body) : undefined,
      });
      if (retry.status === 204) return undefined as T;
      if (!retry.ok) {
        const retryText = await retry.text();
        redirectIfPasswordChangeRequired(retry.status, retryText);
        throw new ApiError(retry.status, retryText);
      }
      return attachUiText(await retry.json());
    }
    if (typeof window !== "undefined" && !onPortalPage()) {
      window.location.href = "/login"; // ADR 0329 D4
    }
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    // อ่าน body ครั้งเดียว — `res.text()` สองครั้งได้สตริงว่างในรอบที่สอง
    const text = await res.text();
    redirectIfPasswordChangeRequired(res.status, text);
    redirectIfWrongHost(res.status, text);
    throw new ApiError(res.status, text);
  }

  // Handle 204 No Content
  if (res.status === 204) return undefined as T;

  // stored server sentences are said in the screen's language (plan/i18n-gaps)
  return attachUiText(await res.json());
}

/**
 * ADR 0264 D4 — 403 `PASSWORD_CHANGE_REQUIRED` = ล้าง session แล้วกลับไปหน้าล็อกอิน
 *
 * เคสที่มาถึงตรงนี้คือ **ผู้ใช้กำลังใช้ระบบอยู่ แล้วผู้ดูแลรีเซ็ตรหัสให้ตอนนั้น** ·
 * request ถัดไปได้ 403 กลางคัน
 *
 * เดิม (ADR 0261 D7) พาไปหน้า `/set-password` โดยยังถือ session ไว้ · ตอนนี้ทางเข้าเหลือ
 * ทางเดียวคือหน้าล็อกอิน — เขาล็อกอินด้วยรหัสที่ผู้ดูแลให้ แล้วตั้งรหัสใหม่บนจอเดียวกัน
 *
 * **ต้อง `clearTokens()` ไม่ใช่แค่ redirect** เพราะสองเหตุผล:
 *  1. โทเคนของคนที่ยังใช้ระบบไม่ได้ ไม่ควรค้างอยู่ในเครื่อง
 *  2. ถ้าไม่ล้าง หน้าล็อกอิน/AuthProvider จะเห็นว่ายังมี session แล้วเด้งกลับเข้าระบบวนไปมา
 *
 * ผลพลอยได้ที่ตั้งใจ: `clearTokens` ประกาศ logout ผ่าน auth-broadcast (ADR 0228) —
 * แท็บอื่นของคนเดียวกันปิดพร้อมกัน ไม่มีแท็บผีที่ยังคิดว่าตัวเองล็อกอินอยู่
 *
 * ต่างจาก 401 ตรงที่ไม่ลอง refresh ก่อน — โทเคนไม่ได้มีปัญหา สิ่งที่ขาดคือรหัสผ่านใหม่
 */
/**
 * ADR 0329 D4 — `/p/**` เป็นอาณาเขตของ passenger-auth
 *
 * ไคลเอนต์นี้พูดกับ API ของ **พนักงาน** · 401 ของมันที่นั่นแปลว่า "ไม่มีเซสชันพนักงาน"
 * ซึ่งเป็นเรื่องปกติของลูกค้าพอร์ทัล ไม่ใช่ "เซสชันตาย" · โยน error ให้ผู้เรียกจัดการ
 * ตามเดิม แค่ไม่ลากผู้ใช้ออกไปหน้า login ของพนักงาน
 *
 * D1–D3 ปิดรูที่รู้จักวันนี้ไปแล้ว (คอมโพเนนต์ร่วมเลิกเรียก API แอดมินเอง) · ข้อนี้คือ
 * ชั้นที่กันรูวันหน้า: ถ้ามีอะไรหลุดเข้าไปอีก ผลคือ "ฟีเจอร์นั้นไม่ทำงาน"
 * ไม่ใช่ "ลูกค้าถูกเตะออกจากระบบ"
 *
 * เทียบ `"/p/"` ที่มีทับปิดท้าย **ห้ามใช้ `"/p"`** — ไม่งั้น `/passengers` และ
 * `/portals` ซึ่งเป็นหน้าแอดมินจะโดนด้วย แล้ว session ที่ตายจริงจะค้างอยู่
 */
function onPortalPage(): boolean {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/p/");
}

function redirectIfPasswordChangeRequired(status: number, body: string): void {
  if (status !== 403) return;
  let code: unknown;
  try {
    // body ของ ApiError เป็น "สตริง" ไม่ใช่ object ที่ parse แล้ว — ต้อง parse เอง
    code = (JSON.parse(body) as { code?: unknown }).code;
  } catch {
    return; // 403 ธรรมดา (สิทธิ์ไม่พอ) — ปล่อยให้ผู้เรียกจัดการเอง ห้ามล้าง session
  }
  if (code !== "PASSWORD_CHANGE_REQUIRED") return;
  if (typeof window === "undefined") return;
  // ADR 0329 D4 — ธง "ต้องเปลี่ยนรหัส" เป็นของบัญชี **พนักงาน** ไม่เกี่ยวกับผู้โดยสาร
  // ล้างโทเคนพนักงานของลูกค้าพอร์ทัลก็ไม่มีความหมาย แต่การลากเขาออกไปมีผลเสียจริง
  if (onPortalPage()) return;
  clearTokens();
  window.location.href = "/login";
}

/**
 * ADR 0418 D8 — the API's host lock said no (TenantGuard → 403 `{error: HOST_ORG_MISMATCH}`):
 * this domain belongs to another org than the one in `x-tenant-id`. Normally the dashboard layout
 * never gets here (resolveLanding sends the user to /wrong-host first) — this catches a stale tab
 * or an edited `active_tenant_id`. The session is kept: the account is fine, the domain is not.
 */
function wrongHostCode(status: number, body: string): boolean {
  if (status !== 403) return false;
  try {
    return (JSON.parse(body) as { error?: unknown }).error === HOST_ORG_MISMATCH;
  } catch {
    return false;
  }
}

function redirectIfWrongHost(status: number, body: string): void {
  if (!wrongHostCode(status, body) || typeof window === "undefined") return;
  if (onPortalPage() || window.location.pathname === "/wrong-host") return;
  localStorage.removeItem("active_tenant_id");
  window.location.replace("/wrong-host");
}

/** เปิดให้เทสเรียกตรง — ตรรกะนี้อยู่ในเส้นทาง error ที่ e2e จำลองยาก */
export const __testing = { redirectIfPasswordChangeRequired, onPortalPage, wrongHostCode };

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`API ${status}: ${body.slice(0, 200)}`);
    this.name = "ApiError";
  }
}

const DEFAULT_FAILURE = "Request failed";
const THAI_LETTER = /[\u0E00-\u0E3E\u0E40-\u0E7F]/;
/** is this text in the language the screen is in? (no letters at all — a code, a number — fits both) */
const fitsScreen = (text: string) => (getDisplayLocale() === "th" ? THAI_LETTER.test(text) || !/[A-Za-z]{3,}/.test(text) : !THAI_LETTER.test(text));

/**
 * The server's own sentence, when no code of it is in the dictionary. Most modules still write it in one
 * language (English, some Thai — plan/i18n-gaps B): said as is when it matches the screen, otherwise led by
 * what failed in the screen's language ("บันทึกไม่สำเร็จ — Employee not found"), the detail kept for support.
 */
function inScreenLanguage(message: string, fallback: string): string {
  if (fitsScreen(message)) return message;
  const head = fallback !== DEFAULT_FAILURE && fitsScreen(fallback) ? fallback : tOutside("common.failed");
  return `${head} — ${message}`;
}


export function getApiErrorMessage(e: unknown, fallback = DEFAULT_FAILURE): string {
  if (e instanceof ApiError) {
    try {
      const parsed = JSON.parse(e.body);
      // the server says every refusal in both languages (`message_msg` — plan/i18n-api-errors): a sentence of its
      // own (`apiError`) or the one for its code (`coded-refusal.ts`); the screen shows its side
      if (isStoredMsg(parsed?.message_msg)) return sayStored(parsed.message_msg);
      if (typeof parsed?.message === "string") return inScreenLanguage(parsed.message, fallback);
      if (Array.isArray(parsed?.message)) return inScreenLanguage(parsed.message.join(", "), fallback);
    } catch {
      // body is not JSON
    }
    return e.body?.slice(0, 200) || `API ${e.status}`;
  }
  if (e instanceof Error) return e.message;
  return fallback;
}

/**
 * Structured `details` from a normalised API error (ADR 0098) — e.g. the
 * `{ conflicts: [...] }` payload on a RESOURCE_NOT_AVAILABLE 409. Returns null
 * when the body isn't JSON or carries no details.
 */
export function getApiErrorDetails<T = unknown>(e: unknown): T | null {
  if (e instanceof ApiError) {
    try {
      const parsed = JSON.parse(e.body);
      return (parsed?.details as T) ?? null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * รหัสความผิดพลาดของ API (`{ error: "CELL_HAS_JOBS", ... }`) — คนละอย่างกับ
 * `message` ซึ่งเป็นข้อความอังกฤษของ server ที่เอามาโชว์บนจอไทยไม่ได้
 *
 * ผู้เรียกใช้รหัสเพื่อเลือกคีย์แปลของตัวเอง แล้วค่อยถอยไปใช้ `message` เมื่อไม่รู้จักรหัส
 */
export function getApiErrorCode(e: unknown): string | null {
  if (e instanceof ApiError) {
    try {
      const parsed = JSON.parse(e.body);
      return typeof parsed?.error === "string" ? parsed.error : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * ฟิลด์ระดับบนสุดอื่น ๆ ของ body error — เช่น `locked_dates` ที่มากับ
 * `DATE_LOCKED` ซึ่งไม่ได้อยู่ใต้ `details`
 *
 * คู่กับ `getApiErrorCode`: รหัสบอกว่าเกิดอะไร ฟิลด์นี้ให้ค่าที่ต้องเอาไปเติมใน
 * ข้อความแปล แทนที่จะโชว์ `message` อังกฤษของ server บนจอไทย
 */
export function getApiErrorField<T = unknown>(e: unknown, key: string): T | null {
  if (e instanceof ApiError) {
    try {
      const parsed = JSON.parse(e.body) as Record<string, unknown>;
      return (parsed?.[key] as T) ?? null;
    } catch {
      return null;
    }
  }
  return null;
}

/** One clashing ticket from an availability check / RESOURCE_NOT_AVAILABLE body. */
export interface AvailabilityConflict {
  ticket_id: string;
  display_id: string;
  status: string;
  start_time_at: string | null;
  expected_end_at: string | null;
  resources: ("driver" | "vehicle")[];
}

// Convenience methods
export const apiGet = <T = unknown>(path: string) => api<T>(path);
// `init` — extra fetch options, รูปแบบเดียวกับ apiPatch ข้างล่าง · ตัวที่ต้องการจริง
// คือ `timeoutMs`: งาน AI optimize ของบอร์ดใช้เวลานานกว่าเพดาน 30 วิของ
// DEFAULT_TIMEOUT_MS ได้ (solver ถูกสั่งให้คิดได้ถึง 30 วิที่
// scheduling.service.ts:212 แล้วบวกเวลาเตรียมข้อมูลอีก) — ไม่ส่งมาก็ได้ค่าตั้งต้นเท่าเดิม
export const apiPost = <T = unknown>(path: string, body?: unknown, init?: Omit<ApiOptions, "body" | "method">) =>
  api<T>(path, { ...init, method: "POST", body });
// `init` — extra fetch options; autosave passes `{ keepalive: true }` so a flush on
// pagehide/unmount survives the page going away (ADR 0214 D8).
export const apiPatch = <T = unknown>(path: string, body?: unknown, init?: Omit<ApiOptions, "body" | "method">) =>
  api<T>(path, { ...init, method: "PATCH", body });
export const apiPut = <T = unknown>(path: string, body?: unknown) =>
  api<T>(path, { method: "PUT", body });
export const apiDelete = <T = unknown>(path: string) =>
  api<T>(path, { method: "DELETE" });
